/* eslint-disable @typescript-eslint/no-explicit-any -- Sanitized snapshot blobs from edge functions; see mem://preferences/lint-debt.md */
import { useEffect, useMemo, useState } from "react";
import {
  getSuppressionDetails,
  summarizeEmailDispatch,
  type ContentPublishingDispatch,
  type SuppressionDetail,
} from "@/lib/composerCore";

export type RecipientRow = {
  email: string;
  status: "accepted" | "rejected" | "bounced" | "complained";
  detail: string | null;
  postEventAt?: string | null;
};

export type PostSendEvent = {
  email: string;
  type: "bounced" | "complained";
  at: string;
};

export const formatJson = (value: unknown) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return String(value ?? "");
  }
};

/** Mirror of the friendly-status labels produced by the Kit edge adapter
 *  (`supabase/functions/_shared/publishing/kit.ts`). Used as a fallback when
 *  the dispatch was recorded before the friendlier server-side messages
 *  shipped, or when only a numeric `status_code` is available. */
export const friendlyHttpStatusLabel = (status: number): string => {
  switch (status) {
    case 0: return "Network error";
    case 400: return "Bad request (400)";
    case 401: return "Unauthorized (401)";
    case 403: return "Forbidden (403)";
    case 404: return "Not found (404)";
    case 408: return "Request timeout (408)";
    case 422: return "Unprocessable (422)";
    case 429: return "Rate limited (429)";
    case 500: return "Kit server error (500)";
    case 502: return "Bad gateway (502)";
    case 503: return "Service unavailable (503)";
    case 504: return "Gateway timeout (504)";
    default:
      if (status >= 500) return `Kit server error (${status})`;
      if (status >= 400) return `Kit client error (${status})`;
      return `HTTP ${status}`;
  }
};

export const parsePostSendEvents = (response: Record<string, unknown>): PostSendEvent[] => {
  const raw = response.post_send_events;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (e): e is PostSendEvent =>
        !!e &&
        typeof e === "object" &&
        typeof (e as any).email === "string" &&
        ((e as any).type === "bounced" || (e as any).type === "complained") &&
        typeof (e as any).at === "string",
    )
    .map((e) => ({ email: e.email.toLowerCase(), type: e.type, at: e.at }));
};

/** Builds the sorted recipient table from a parsed email dispatch summary
 *  plus any post-send bounce/complaint events. */
export const useRecipientRows = (
  emailSummary: ReturnType<typeof summarizeEmailDispatch> | null,
  postSendEvents: PostSendEvent[],
): RecipientRow[] => {
  return useMemo<RecipientRow[]>(() => {
    if (!emailSummary) return [];
    const rejectedMap = new Map(emailSummary.rejections.map((r) => [r.email.toLowerCase(), r.reason]));
    const messageIdByEmail = new Map<string, string>();
    for (const id of emailSummary.messageIds) {
      const match = id.match(/:([^:]+@[^:]+)$/);
      if (match) messageIdByEmail.set(match[1].toLowerCase(), id);
    }
    const eventByEmail = new Map<string, PostSendEvent>();
    for (const ev of postSendEvents) {
      const prev = eventByEmail.get(ev.email);
      if (!prev || new Date(ev.at).getTime() > new Date(prev.at).getTime()) {
        eventByEmail.set(ev.email, ev);
      }
    }
    const rows: RecipientRow[] = emailSummary.recipients.map((email) => {
      const lower = email.toLowerCase();
      if (rejectedMap.has(lower)) {
        return { email, status: "rejected", detail: rejectedMap.get(lower) ?? null };
      }
      const event = eventByEmail.get(lower);
      if (event) {
        const verb = event.type === "bounced" ? "Bounced after delivery" : "Marked as spam after delivery";
        const when = (() => {
          try { return new Date(event.at).toLocaleString(); } catch { return event.at; }
        })();
        return { email, status: event.type, detail: `${verb} · ${when}`, postEventAt: event.at };
      }
      return { email, status: "accepted", detail: messageIdByEmail.get(lower) ?? null };
    });
    const rank = (s: RecipientRow["status"]) =>
      s === "rejected" ? 0 : s === "bounced" ? 1 : s === "complained" ? 2 : 3;
    rows.sort((a, b) => {
      const ra = rank(a.status);
      const rb = rank(b.status);
      if (ra !== rb) return ra - rb;
      return a.email.localeCompare(b.email);
    });
    return rows;
  }, [emailSummary, postSendEvents]);
};

/** Loads workspace suppression records for any bounced/complained recipient
 *  in the current dispatch so the UI can show a hover diagnostic. */
export const useSuppressionDetails = (
  open: boolean,
  dispatch: ContentPublishingDispatch | null,
  recipientRows: RecipientRow[],
) => {
  const suppressedEmails = useMemo(
    () =>
      recipientRows
        .filter((r) => r.status === "bounced" || r.status === "complained")
        .map((r) => r.email.toLowerCase()),
    [recipientRows],
  );
  const [suppressionByEmail, setSuppressionByEmail] = useState<Record<string, SuppressionDetail>>({});
  const [suppressionLoading, setSuppressionLoading] = useState(false);

  useEffect(() => {
    if (!open || !dispatch || suppressedEmails.length === 0) {
      setSuppressionByEmail({});
      return;
    }
    let cancelled = false;
    setSuppressionLoading(true);
    void getSuppressionDetails(dispatch.workspace_id, suppressedEmails)
      .then((rows) => {
        if (cancelled) return;
        const map: Record<string, SuppressionDetail> = {};
        for (const r of rows) map[r.email] = r;
        setSuppressionByEmail(map);
      })
      .catch(() => {
        if (!cancelled) setSuppressionByEmail({});
      })
      .finally(() => {
        if (!cancelled) setSuppressionLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dispatch, suppressedEmails.join("|")]);

  return { suppressionByEmail, suppressionLoading };
};
